import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import { CustomFieldDefinitionTypes } from '@nx-smartmonkey/shared/domain';
import { HSpacer, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useState } from 'react';

import { Project } from '../../../../domain/project/Project';
import {
  ProjectCustomersFieldTypes,
  ProjectCustomersStopCanceledType,
  ProjectCustomersStopFieldType,
  projectCustomersStopCanceledValues,
} from '../../../../domain/project/projectCustomers/ProjectCustomersFields';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { useUpdateProject } from '../../../../hooks/project/useUpdateProject';
import ProjectCustomersMapper from '../../../../mappers/project/ProjectCustomersMapper';
import { Accordion } from '../../../../react-components/Accordion';
import Button from '../../../../react-components/Button';
import Text from '../../../../react-components/Text';
import { AppField, ManageFieldsDialog } from './ManageFieldsDialog';
import { SortableOptionContainer } from './pills/sortable/SortableOptionContainer';

interface CustomersStopCanceledFieldsProps {
  project: Project;
}

export const CustomersStopCanceledFields = ({ project }: CustomersStopCanceledFieldsProps) => {
  const { run: editProject } = useUpdateProject({ project });
  const [manageFieldsDialogOpen, setManageFieldsDialogOpen] = useState<boolean>(false);

  const getAllFields = (): AppField<ProjectCustomersStopCanceledType>[] => {
    const fields: AppField<ProjectCustomersStopFieldType>[] = Object.entries(projectCustomersStopCanceledValues).map(
      ([id, type]) => {
        const foundField = project.customers.stop_canceled_fields.find((f) => f.id === id);

        return {
          id,
          text: intl.formatMessage({ id: `customers.app.options.stop_canceled_fields.${id}` }),
          enabled: Boolean(foundField),
          type: type as ProjectCustomersFieldTypes,
          is_custom_field: false,
        };
      }
    );

    project.custom_fields?.service_report_canceled
      .filter((cField) => cField.enabled)
      .forEach((cField) => {
        const foundField = project.customers.stop_canceled_fields.find((f) => f.id === cField.custom_id);

        fields.push({
          id: cField.custom_id,
          text: cField.label,
          enabled: Boolean(foundField),
          type: cField.field_type as CustomFieldDefinitionTypes,
          is_custom_field: true,
        });
      });

    return fields;
  };

  const getAllActiveFields = (): AppField<ProjectCustomersStopCanceledType>[] => {
    return project.customers.stop_canceled_fields
      .filter((field) => {
        if (!field.is_custom_field) return true;

        // Filtering the fields of type "custom field" that are enabled
        const cFieldFound = project.custom_fields?.service_report_canceled.find(
          (cField) => cField.enabled && cField.custom_id === field.id
        );
        return Boolean(cFieldFound);
      })
      .map((field) => {
        if (!field.is_custom_field) {
          return {
            id: field.id,
            text: intl.formatMessage({ id: `customers.app.options.stop_canceled_fields.${field.id}` }),
            enabled: true,
            type: field.type,
            is_custom_field: false,
          };
        }

        const cFieldFound = project.custom_fields?.service_report_canceled.find(
          (cField) => cField.enabled && cField.custom_id === field.id
        );

        return {
          id: cFieldFound!.custom_id,
          text: cFieldFound!.label ?? ``,
          enabled: true,
          type: cFieldFound!.field_type,
          is_custom_field: true,
        };
      });
  };

  return (
    <div
      style={{
        border: `1px solid #E0E0E0`,
        borderRadius: `2px`,
      }}
    >
      <Accordion
        id="customers_app_stop_info"
        expanded
        headerStyles={{
          position: `relative`,
          paddingTop: `16px`,
        }}
        headerContent={
          <Text
            style={{ cursor: `pointer` }}
            variant="body-large"
            isBold
            intlId="customers.app.options.stop_canceled_fields"
          />
        }
        contentStyle={{
          padding: `16px`,
          borderTop: `1px solid #E0E0E0`,
        }}
      >
        <Text variant="label" intlId="customers.app.options.stop_canceled_fields.info" />

        <VSpacer small />

        <SortableOptionContainer
          listId="customers.app.options.stop_canceled_fields"
          onDragEnd={(fieldId: string, index?: number) => {
            if (index !== undefined) {
              const projectCustomers = ProjectCustomersMapper.toDTO(project.customers);

              const fieldIndex = projectCustomers.stop_canceled_fields.findIndex((field) => field.id === fieldId);
              const field = projectCustomers.stop_canceled_fields[fieldIndex];

              projectCustomers.stop_canceled_fields.splice(fieldIndex, 1);
              projectCustomers.stop_canceled_fields.splice(index, 0, field);

              editProject({
                customers: ProjectCustomersMapper.toDomain({
                  ...ProjectCustomersMapper.toDTO(project.customers),
                  stop_canceled_fields: projectCustomers.stop_canceled_fields,
                }),
              });
            }
          }}
          fields={getAllActiveFields()}
        />

        <Button styleType="secondary" onClick={() => setManageFieldsDialogOpen(true)}>
          <CheckBoxOutlinedIcon />
          <HSpacer small />
          <Text
            intlId="customers.app.options.manage_fields"
            style={{ color: RoutalPalette.neutral00, cursor: `pointer` }}
          />
        </Button>
      </Accordion>

      <ManageFieldsDialog
        open={manageFieldsDialogOpen}
        onClose={() => setManageFieldsDialogOpen(false)}
        fields={getAllFields()}
        onUpdate={({ field, selected }) => {
          const projectCustomers = ProjectCustomersMapper.toDTO(project.customers);

          if (selected) {
            const { text, enabled, ...rest } = field;
            projectCustomers.stop_canceled_fields.push(rest);
          } else {
            projectCustomers.stop_canceled_fields = projectCustomers.stop_canceled_fields.filter(
              (f) => f.id !== field.id
            );
          }

          editProject({
            customers: ProjectCustomersMapper.toDomain(projectCustomers),
          });
        }}
        onUpdateAll={({ selected }) => {
          const projectCustomers = ProjectCustomersMapper.toDTO(project.customers);

          if (selected) {
            projectCustomers.stop_canceled_fields = getAllFields().map((field) => {
              const { text, enabled, ...rest } = field;
              return rest;
            });
          } else {
            projectCustomers.stop_canceled_fields = [];
          }

          editProject({
            customers: ProjectCustomersMapper.toDomain(projectCustomers),
          });
        }}
      />
    </div>
  );
};
